import {
  createTheme,
  SimplePaletteColorOptions,
  ThemeOptions,
} from '@mui/material';

import { DocumentStatus } from '@/types/document';
import { Color as ColorType, GradientColor, StatusColor } from '@/types/theme';

import {
  darkBlueGradient,
  forestGradient,
  neutral,
  orange,
  pink,
  primary,
  purple,
  red,
  semanticCaution,
  semanticError,
  semanticSuccess,
  semanticWarning,
  tiberGradient,
  violet,
  yellow,
} from './constants/colors';

declare module '@mui/material' {
  interface Color {
    1000: string;
    overlayOnLight: string;
    overlayOnDark: string;
    min: string;
    max: string;
  }

  interface Palette {
    primary1000: SimplePaletteColorOptions;
    primary900: SimplePaletteColorOptions;
    primary800: SimplePaletteColorOptions;
    primary500: SimplePaletteColorOptions;
    primary400: SimplePaletteColorOptions;
    primary200: SimplePaletteColorOptions;
    primaries: ColorType;
    secondaryPink: ColorType;
    secondaryPurple: ColorType;
    secondaryViolet: ColorType;
    yellow: ColorType;
    /** @deprecated Use semantic.success instead */
    green: ColorType;
    /** @deprecated Use semantic.error instead */
    red: ColorType;
    /** @deprecated Use neutral instead */
    grey: Color;
    orange: ColorType;
    violet: ColorType;
    neutral: ColorType;
    badgeBackground: GradientColor;
    status: Record<string, StatusColor>;
    overlay: {
      darkBlue: GradientColor;
      tiber: GradientColor;
      forest: GradientColor;
    };
    semantic: {
      success: ColorType;
      error: ColorType;
      warning: ColorType;
      caution: ColorType;
    };
  }
  interface PaletteOptions {
    primary1000: SimplePaletteColorOptions;
    primary900: SimplePaletteColorOptions;
    primary800: SimplePaletteColorOptions;
    primary500: SimplePaletteColorOptions;
    primary400: SimplePaletteColorOptions;
    primary200: SimplePaletteColorOptions;
    primaries: ColorType;
    secondaryPink: ColorType;
    secondaryPurple: ColorType;
    secondaryViolet: ColorType;
    yellow: ColorType;
    green: ColorType;
    red: ColorType;
    orange: ColorType;
    violet: ColorType;
    neutral: ColorType;
    badgeBackground: GradientColor;
    semantic: {
      success: ColorType;
      error: ColorType;
      warning: ColorType;
      caution: ColorType;
    };
    status: Record<string, StatusColor>;
    overlay: {
      darkBlue: GradientColor;
      tiber: GradientColor;
      forest: GradientColor;
    };
  }
}

const {
  palette: { augmentColor },
} = createTheme();

export const paletteConfigs: ThemeOptions['palette'] = {
  mode: 'light',
  primary: augmentColor({ color: primary }),
  primary1000: augmentColor({ color: primary, mainShade: 1000 }),
  primary900: augmentColor({ color: primary, mainShade: 900 }),
  primary800: augmentColor({ color: primary, mainShade: 800 }),
  primary500: augmentColor({ color: primary, mainShade: 500 }),
  primary400: augmentColor({ color: primary, mainShade: 400 }),
  primary200: augmentColor({ color: primary, mainShade: 200 }),
  secondary: augmentColor({ color: pink }),
  success: augmentColor({ color: semanticSuccess }),
  error: augmentColor({ color: red }),
  warning: augmentColor({ color: yellow }),
  info: augmentColor({ color: primary }),
  primaries: primary,
  secondaryPink: pink,
  secondaryPurple: purple,
  secondaryViolet: violet,
  yellow: yellow,
  green: semanticSuccess,
  red: red,
  orange: orange,
  /** @deprecated Use neutral instead */
  grey: neutral,
  violet: violet,
  semantic: {
    success: semanticSuccess,
    error: semanticError,
    warning: semanticWarning,
    caution: semanticCaution,
  },
  background: {
    default: neutral.min,
    paper: neutral.min,
  },
  common: {
    white: neutral.min,
    black: neutral.max,
  },
  text: {
    primary: neutral[1000],
    secondary: neutral[700],
    disabled: neutral[700],
  },
  action: {
    disabledBackground: neutral[200],
    disabled: neutral[400],
  },
  badgeBackground: darkBlueGradient,
  status: {
    verified: {
      background: semanticSuccess[100],
      text: semanticSuccess[500],
    },
    unverified: {
      background: orange[100],
      text: orange[600],
    },
    [DocumentStatus.Rejected]: {
      text: red[600],
      background: red[100],
      icon: red[500],
      iconBackground: red[200],
      iconBorder: red[500],
    },
    [DocumentStatus.Approved]: {
      main: semanticSuccess[500],
      background: semanticSuccess[100],
      text: semanticSuccess[500],
      icon: semanticSuccess[500],
      iconBackground: semanticSuccess[200],
    },
    [DocumentStatus.Completed]: {
      main: semanticSuccess[500],
      background: semanticSuccess[100],
      text: semanticSuccess[500],
      icon: semanticSuccess[500],
      iconBackground: semanticSuccess[200],
    },
    [DocumentStatus.Expired]: {
      main: red[500],
      background: red[500],
      text: neutral.min,
      icon: red[500],
      iconBackground: red[200],
      iconBorder: red[500],
    },
    [DocumentStatus.InReview]: {
      main: yellow[700],
      background: yellow[100],
      text: yellow[700],
      icon: yellow[700],
      iconBackground: yellow[100],
    },
    primary: {
      text: primary[400],
      icon: primary[400],
      iconBackground: primary[100],
    },
    grey: {
      text: neutral[1000],
      icon: neutral[700],
      iconBackground: neutral[100],
    },
  },
  overlay: {
    darkBlue: darkBlueGradient,
    tiber: tiberGradient,
    forest: forestGradient,
  },
  neutral,
};
